import { type License, WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import pluralize from "pluralize";

import { type WorkerRequirement } from "../../Documents/api/types";
import { RoleListItemDescriptionRow } from "./DescriptionRow";

interface RoleListItemLicensesInfoProps {
  licenses: License[];
  requiresLicense: boolean;
  baseCredential: WorkerRequirement | undefined;
}

interface GroupedLicenses {
  needsAttentionLicenses: License[];
  pendingLicenses: License[];
  activeLicenses: License[];
}

export function RoleListItemLicensesInfo(props: RoleListItemLicensesInfoProps) {
  const { licenses, requiresLicense, baseCredential } = props;

  if (!requiresLicense && !isDefined(baseCredential)) {
    return (
      <RoleListItemDescriptionRow
        iconType="checkmark"
        intent="success"
        text="Approved to work shifts! This role does not require any specific licenses."
      />
    );
  }

  if (!requiresLicense && isDefined(baseCredential)) {
    return baseCredential.status === WorkerRequirementStatus.MISSING ? (
      <RoleListItemDescriptionRow
        iconType="triangle-exclamation"
        intent="warning"
        text="Qualification required"
      />
    ) : (
      <RoleListItemDescriptionRow
        iconType="checkmark"
        intent="success"
        text="Qualification uploaded"
      />
    );
  }

  const hasNoLicenses = licenses.every(
    (license) => license.attributes.status === LicenseStatuses.ARCHIVED
  );

  const { needsAttentionLicenses, pendingLicenses, activeLicenses } =
    licenses.reduce<GroupedLicenses>(
      (accumulator, license) => {
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (license.attributes.status) {
          case "PENDING": {
            accumulator.pendingLicenses.push(license);
            break;
          }

          case "EXPIRING":
          case "EXPIRED":
          case "REJECTED": {
            accumulator.needsAttentionLicenses.push(license);
            break;
          }

          case "ACTIVE": {
            accumulator.activeLicenses.push(license);
            break;
          }

          default: {
            break;
          }
        }

        return accumulator;
      },
      {
        activeLicenses: [],
        needsAttentionLicenses: [],
        pendingLicenses: [],
      }
    );

  return (
    <Stack
      sx={{
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 1,
      }}
    >
      {hasNoLicenses && (
        <RoleListItemDescriptionRow
          iconType="triangle-exclamation"
          intent="error"
          text="License required"
        />
      )}

      {needsAttentionLicenses.length > 0 && (
        <RoleListItemDescriptionRow
          iconType="triangle-exclamation"
          intent="error"
          text={`${needsAttentionLicenses.length} ${pluralize(
            "license",
            needsAttentionLicenses.length
          )} ${needsAttentionLicenses.length === 1 ? "needs" : "need"} your attention`}
        />
      )}

      {pendingLicenses.length > 0 && (
        <RoleListItemDescriptionRow
          iconType="document-lens"
          intent="neutral"
          text={`${pendingLicenses.length} ${pluralize(
            "license",
            pendingLicenses.length
          )} in review`}
        />
      )}

      {activeLicenses.length > 0 && (
        <RoleListItemDescriptionRow
          iconType="checkmark"
          intent="success"
          text={`${activeLicenses.length} active ${pluralize("license", activeLicenses.length)}`}
        />
      )}
    </Stack>
  );
}
